import React, { useState } from 'react';
import { motion } from 'framer-motion';
import Icon from '../../../components/AppIcon';
import CalendlyWidget from 'components/CalendlyWidget';

const ProcessVisualization = () => {
  const [activeStep, setActiveStep] = useState(0);

  const processSteps = [
    {
      id: 1,
      title: "Discovery & Analysis",
      subtitle: "Understanding Your Vision",
      description: `We begin every project with comprehensive discovery sessions to understand your business objectives, technical requirements, and user needs.

Our team conducts stakeholder interviews, competitive analysis, and technical feasibility studies to create a solid foundation for your project.`,
      icon: "Search",
      color: "accent",
      duration: "1-2 weeks",
      deliverables: [
        "Project Requirements Document",
        "Technical Architecture Plan",
        "User Journey Mapping",
        "Competitive Analysis Report"
      ],
      tools: ["Miro", "Figma", "Notion", "Slack"]
    },
    {
      id: 2,
      title: "Design & Prototyping",
      subtitle: "Bringing Ideas to Life",
      description: `Transform requirements into tangible designs through wireframing, prototyping, and user experience design that aligns with your brand and business goals.

We create interactive prototypes that allow you to experience the solution before development begins, ensuring alignment and reducing revision cycles.`,
      icon: "Palette",
      color: "success",
      duration: "2-3 weeks",
      deliverables: [
        "Wireframes & User Flows",
        "Interactive Prototypes",
        "Design System & Style Guide",
        "Usability Testing Results"
      ],
      tools: ["Figma", "Adobe XD", "InVision", "Principle"]
    },
    {
      id: 3,
      title: "Development & Integration",
      subtitle: "Building Your Solution",
      description: `Our development team brings designs to life using modern technologies and best practices, with regular check-ins and iterative delivery to ensure quality and alignment.

We follow agile methodologies with continuous integration and deployment practices to deliver reliable, scalable solutions.`,
      icon: "Code2",
      color: "warning",
      duration: "4-12 weeks",
      deliverables: [
        "Working Software Increments",
        "API Documentation",
        "Code Repository & Documentation",
        "Automated Testing Suite"
      ],
      tools: ["VS Code", "Git", "Docker", "AWS"]
    },
    {
      id: 4,
      title: "Testing & Quality Assurance",
      subtitle: "Ensuring Excellence",
      description: `Comprehensive testing across multiple devices, browsers, and scenarios to ensure your solution performs flawlessly under real-world conditions.

Our QA process includes automated testing, manual testing, performance optimization, and security audits to deliver production-ready solutions.`,
      icon: "Shield",
      color: "purple-500",
      duration: "1-2 weeks",
      deliverables: [
        "Test Plans & Results",
        "Performance Audit Report",
        "Security Assessment",
        "Cross-browser Compatibility Report"
      ],
      tools: ["Jest", "Cypress", "Lighthouse", "OWASP ZAP"]
    },
    {
      id: 5,
      title: "Deployment & Launch",
      subtitle: "Going Live Successfully",
      description: `Seamless deployment to production environments with monitoring, backup strategies, and launch support to ensure a smooth transition from development to live operation.

We provide comprehensive launch support including performance monitoring, user training, and immediate post-launch optimization.`,
      icon: "Rocket",
      color: "emerald-500",
      duration: "1 week",
      deliverables: [
        "Production Deployment",
        "Monitoring & Analytics Setup",
        "User Training Materials",
        "Launch Support Documentation"
      ],
      tools: ["AWS", "Vercel", "Google Analytics", "Sentry"]
    },
    {
      id: 6,
      title: "Support & Optimization",
      subtitle: "Continuous Improvement",
      description: `Ongoing support, maintenance, and optimization to ensure your solution continues to perform excellently and evolves with your business needs.

Our support includes regular updates, performance monitoring, feature enhancements, and strategic consultation for future growth.`,
      icon: "Settings",
      color: "blue-500",
      duration: "Ongoing",
      deliverables: [
        "Regular Maintenance Updates",
        "Performance Reports",
        "Feature Enhancement Plans",
        "Strategic Consultation"
      ],
      tools: ["Monitoring Tools", "Analytics", "Support Portal", "Slack"]
    }
  ];

  const currentStep = processSteps?.[activeStep];

  return (
    <section className="py-20 bg-gradient-to-b from-primary/5 to-background">
      <div className="container mx-auto px-6 lg:px-8">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <div className="inline-flex items-center space-x-2 bg-accent/10 text-accent px-4 py-2 rounded-full text-sm font-medium mb-4">
            <Icon name="GitBranch" size={16} />
            <span>Development Methodology</span>
          </div>
          <h2 className="text-3xl lg:text-5xl font-bold text-foreground mb-6">
            Our Proven
            <span className="block bg-gradient-to-r from-accent to-success bg-clip-text text-transparent">
              Development Process
            </span>
          </h2>
          <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
            A systematic approach to software development that ensures quality, 
            transparency, and successful project delivery every time.
          </p>
        </motion.div>

        {/* Process Timeline */}
        <div className="relative">
          {/* Timeline Line */}
          <div className="hidden lg:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-gradient-to-b from-accent via-success to-warning rounded-full opacity-20"></div>

          {/* Process Steps */}
          <div className="space-y-12 lg:space-y-20">
            {processSteps?.map((step, index) => (
              <motion.div
                key={step?.id}
                initial={{ opacity: 0, y: 50 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: index * 0.1 }}
                className={`relative grid lg:grid-cols-2 gap-8 items-center ${
                  index % 2 === 0 ? 'lg:text-right' : 'lg:text-left'
                }`}
              >
                {/* Timeline Node */}
                <div className="hidden lg:block absolute left-1/2 top-1/2 transform -translate-x-1/2 -translate-y-1/2 z-10">
                  <motion.button
                    onClick={() => setActiveStep(index)}
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.95 }}
                    className={`w-16 h-16 rounded-full border-4 transition-all duration-300 ${
                      index === activeStep
                        ? `bg-${step?.color} border-${step?.color} shadow-lg shadow-${step?.color}/30`
                        : 'bg-background border-border hover:border-accent'
                    }`}
                  >
                    <Icon 
                      name={step?.icon} 
                      size={24} 
                      className={index === activeStep ? 'text-white' : 'text-muted-foreground'} 
                    />
                  </motion.button>
                </div>

                {/* Step Content */}
                <div className={`space-y-6 ${index % 2 === 0 ? 'lg:order-1' : 'lg:order-2'}`}>
                  <div className="space-y-4">
                    <div className={`inline-flex items-center space-x-2 bg-${step?.color}/10 text-${step?.color} px-4 py-2 rounded-full text-sm font-medium`}>
                      <Icon name={step?.icon} size={16} />
                      <span>Step {step?.id}</span>
                    </div>
                    
                    <h3 className="text-2xl lg:text-3xl font-bold text-foreground">
                      {step?.title}
                    </h3>
                    
                    <p className={`text-${step?.color} font-medium text-lg`}>
                      {step?.subtitle}
                    </p>
                    
                    <p className="text-muted-foreground leading-relaxed whitespace-pre-line">
                      {step?.description}
                    </p>
                  </div>

                  {/* Duration */}
                  <div className="flex items-center space-x-2">
                    <Icon name="Clock" size={16} className="text-muted-foreground" />
                    <span className="text-sm text-muted-foreground">Duration: {step?.duration}</span>
                  </div>
                </div>

                {/* Step Details */}
                <div className={`space-y-6 ${index % 2 === 0 ? 'lg:order-2' : 'lg:order-1'}`}>
                  <div className={`bg-gradient-to-br from-${step?.color}/10 to-${step?.color}/5 border border-${step?.color}/20 rounded-xl p-6`}>
                    {/* Deliverables */}
                    <div className="space-y-4">
                      <h4 className="font-semibold text-foreground flex items-center space-x-2">
                        <Icon name="Package" size={16} />
                        <span>Key Deliverables</span>
                      </h4>
                      <div className="space-y-2">
                        {step?.deliverables?.map((deliverable, idx) => (
                          <div key={idx} className="flex items-center space-x-3">
                            <div className={`w-2 h-2 bg-${step?.color} rounded-full`}></div>
                            <span className="text-sm text-muted-foreground">{deliverable}</span>
                          </div>
                        ))}
                      </div>
                    </div>

                    {/* Tools */}
                    <div className="space-y-4 pt-4 border-t border-border">
                      <h4 className="font-semibold text-foreground flex items-center space-x-2">
                        <Icon name="Wrench" size={16} />
                        <span>Tools & Technologies</span>
                      </h4>
                      <div className="flex flex-wrap gap-2">
                        {step?.tools?.map((tool, idx) => (
                          <span
                            key={idx}
                            className={`px-3 py-1 bg-${step?.color}/20 text-${step?.color} text-xs rounded-full border border-${step?.color}/30`}
                          >
                            {tool}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Process Summary */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.8 }}
          className="mt-20 text-center"
        >
          <div className="bg-gradient-to-r from-accent/10 via-success/10 to-warning/10 border border-accent/20 rounded-2xl p-8 max-w-4xl mx-auto">
            <h3 className="text-2xl font-bold text-foreground mb-4">
              Ready to Start Your Project?
            </h3>
            <p className="text-muted-foreground mb-6">
              Our proven process ensures successful delivery of your software solution. 
              Let's discuss how we can bring your vision to life.
            </p>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                // className="bg-accent text-accent-foreground px-6 py-3 rounded-lg font-medium shadow-accent hover:bg-accent/90 transition-colors flex items-center justify-center space-x-2"
              >
                {/* insering calendly */}
                <CalendlyWidget
                      buttonText="Schedule Consultation"
                      buttonClassName="bg-accent text-accent-foreground px-6 py-3 rounded-lg font-medium shadow-accent hover:bg-accent/90 transition-colors flex items-center justify-center space-x-2"/>
                {/* <Icon name="MessageCircle" size={20} />
                <span>Schedule Consultation</span> */}
              </motion.button>
              <motion.button
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
                className="border border-accent/30 text-accent px-6 py-3 rounded-lg font-medium hover:bg-accent/10 transition-colors flex items-center justify-center space-x-2"
              >
                <Icon name="Download" size={20} />
                <span>Download Process Guide</span>
              </motion.button>
            </div>
          </div>
        </motion.div>
      </div>
    </section>
  );
};

export default ProcessVisualization;